import { useState, useEffect } from "react";
import BaseModal from "./BaseModal";
import Icon from "@mdi/react";
import { mdiLogin } from "@mdi/js";
import { login } from "../auth/auth";
import { useAuth } from "../contexts/AuthContext";

/**
 * GuestLoginModal
 *
 * Shown on the ticket purchase page when a guest enters an email that belongs
 * to an existing Dance Localé account. Lets them authenticate inline without
 * leaving the purchase flow.
 *
 * After successful login the AuthContext is updated (isAuthenticated becomes true),
 * the guest form disappears, and purchase continues as an authenticated user.
 */
const GuestLoginModal = ({ isOpen, onClose, prefillEmail, onLoginSuccess }) => {
    const { login: setUserInContext } = useAuth();
    const [email, setEmail] = useState(prefillEmail || "");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    // Sync email and reset form whenever the modal opens
    useEffect(() => {
        if (isOpen) {
            setEmail(prefillEmail || "");
            setPassword("");
            setError("");
        }
    }, [isOpen, prefillEmail]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            setError("Please enter your email and password.");
            return;
        }

        setIsLoading(true);
        setError("");

        try {
            const result = await login(email, password);

            if (!result.success) {
                setError(result.message || "Incorrect email or password. Please try again.");
                return;
            }

            // Store tokens and update global auth context
            localStorage.setItem("accessToken", result.accessToken);
            localStorage.setItem("refreshToken", result.refreshToken);
            setUserInContext({ ...result.user, accountDetails: result.accountDetails });

            // Let the parent know login succeeded so it can proceed with purchase
            if (onLoginSuccess) onLoginSuccess();
            onClose();
        } catch (err) {
            setError("Login failed. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            title="Log in to continue"
            icon={mdiLogin}
            maxWidth={440}
            footer={
                <>
                    <button
                        className="base-modal-cancel-btn"
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        Continue as guest
                    </button>
                    <button
                        className="base-modal-save-btn"
                        onClick={handleSubmit}
                        disabled={isLoading}
                    >
                        {isLoading ? "Logging in..." : "Log in"}
                    </button>
                </>
            }
        >
            <p style={{ marginBottom: "1.25rem", color: "var(--dl-color-text-muted)", fontSize: "0.9rem" }}>
                This email is linked to a Dance Local&eacute; account. Log in to track your tickets in the app.
            </p>

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                <div>
                    <label style={{ display: "block", fontWeight: "600", marginBottom: "0.25rem", fontSize: "0.875rem" }}>
                        Email
                    </label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                        className="form-input"
                        style={{ width: "100%", padding: "0.5rem 0.75rem", borderRadius: "6px", border: "1px solid var(--dl-color-lightplus)", fontSize: "0.875rem" }}
                    />
                </div>
                <div>
                    <label style={{ display: "block", fontWeight: "600", marginBottom: "0.25rem", fontSize: "0.875rem" }}>
                        Password
                    </label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        className="form-input"
                        autoFocus
                        style={{ width: "100%", padding: "0.5rem 0.75rem", borderRadius: "6px", border: "1px solid var(--dl-color-lightplus)", fontSize: "0.875rem" }}
                    />
                </div>

                {error && (
                    <p style={{ color: "var(--dl-color-danger)", fontSize: "0.825rem", margin: 0 }}>
                        {error}
                    </p>
                )}
            </form>

            <p style={{ marginTop: "1rem", fontSize: "0.8rem", color: "var(--dl-color-text-muted)" }}>
                Forgot your password?{" "}
                <a
                    href="/login"
                    style={{ color: "var(--dl-color-purple-1000)", textDecoration: "underline" }}
                    onClick={onClose}
                >
                    Go to login page
                </a>
            </p>
        </BaseModal>
    );
};

export default GuestLoginModal;
